import { useEffect, useMemo, useId, useRef } from "react";
import { BackgroundWrapper } from "./WavesBackgroundStyles";

type RibbonProps = {
  lines: number;
  spread: number;
  strokeBase: number;
  opacity: number;
  rotate: number;
  drift: number;
  speed: number;
  samples?: number;
};

type Pt = { x: number; y: number };
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

function spine(width: number, height: number) {
  return (t: number): Pt => {
    const x = t * width;
    const env = 1.0 + 2.35 * Math.exp(-Math.pow((t - 0.5) / 0.2, 2));
    const y =
      height * 0.52 +
      env *
        (Math.sin(t * Math.PI * 2) * 28 +
          Math.sin(t * Math.PI * 3.2) * 34 +
          Math.sin(t * Math.PI * 6.4) * 10);
    return { x, y };
  };
}
function dSpine(s: (t: number) => Pt, t: number, eps = 1e-3): Pt {
  const a = s(Math.max(0, t - eps)),
    b = s(Math.min(1, t + eps));
  return { x: (b.x - a.x) / (2 * eps), y: (b.y - a.y) / (2 * eps) };
}

function buildRibbonPathsNormal(
  lines: number,
  spread: number,
  width: number,
  height: number,
  samples = 96 // ↓ de 150 para 96 (ou menos no modo econômico)
) {
  const s = spine(width, height);
  const paths: string[] = [];
  const half = (lines - 1) / 2;

  for (let i = 0; i < lines; i++) {
    const o = (i - half) * spread;
    let seg = "";
    for (let k = 0; k <= samples; k++) {
      const u = k / samples;
      // easeInOut mais simples (custos iguais, mas reduz flicker)
      const t = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
      const p = s(t);
      const dv = dSpine(s, t);
      const nlen = Math.hypot(-dv.y, dv.x) || 1;
      const nx = -dv.y / nlen,
        ny = dv.x / nlen;

      const taper = lerp(0.65, 1.08, Math.exp(-Math.pow((t - 0.5) / 0.6, 2)));
      const x = p.x + nx * o * taper;
      const y = p.y + ny * o * taper;
      seg +=
        k === 0
          ? `M${x.toFixed(1)},${y.toFixed(1)}`
          : `L${x.toFixed(1)},${y.toFixed(1)}`;
    }
    paths.push(seg);
  }
  return paths;
}

function Ribbon({
  idPrefix,
  cfg,
  viewW,
  viewH,
  gradientId,
}: {
  idPrefix: string;
  cfg: RibbonProps;
  viewW: number;
  viewH: number;
  gradientId: string;
}) {
  const { lines, spread, strokeBase, opacity, rotate, drift, speed, samples } =
    cfg;
  const paths = useMemo(
    () => buildRibbonPathsNormal(lines, spread, viewW, viewH, samples ?? 96),
    [lines, spread, viewW, viewH, samples]
  );

  return (
    <g
      className="ribbon-group"
      style={{
        transformOrigin: "50% 50%",
        transform: `rotate(${rotate}deg)`,
        animationDuration: `${18 + drift}s`,
        animationPlayState: "var(--play, running)",
      }}
    >
      {paths.map((d, i) => {
        const dur = speed + (i % 12) * 0.35;
        const delay = -(i * 0.12);
        const mid = (lines - 1) / 2;
        const thicknessBoost =
          1 + 0.24 * Math.cos(((i - mid) / Math.max(mid, 1)) * Math.PI);
        const localOpacity =
          opacity *
          (0.86 + 0.14 * Math.cos((i / Math.max(lines - 1, 1)) * Math.PI));
        return (
          <path
            key={`${idPrefix}-${i}`}
            d={d}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeBase * thicknessBoost}
            strokeOpacity={localOpacity}
            strokeLinecap="round"
            className="wave-line"
            pathLength={1000} // padroniza dash
            style={{
              ["--wave-duration" as any]: `${dur}s`,
              ["--wave-delay" as any]: `${delay}s`,
              animationPlayState: "var(--play, running)",
            }}
          />
        );
      })}
    </g>
  );
}

/** Heurística leve para “modo econômico” */
function useLowPowerMode() {
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const narrow = typeof window !== "undefined" && window.innerWidth < 1024;
  const lowCores =
    typeof navigator !== "undefined" &&
    // alguns navegadores não expõem; trate como false nesse caso
    (navigator as any).hardwareConcurrency &&
    (navigator as any).hardwareConcurrency <= 4;

  return reduced || narrow || lowCores;
}

function WavesBackground() {
  const uid = useId();
  const lowPower = useLowPowerMode();
  const playRef = useRef<HTMLElement | null>(null);

  // Pausar quando a aba não está visível (economiza MUITO)
  useEffect(() => {
    const onVis = () => {
      document.documentElement.style.setProperty(
        "--play",
        document.hidden ? "paused" : "running"
      );
    };
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Viewbox um pouco menor no modo econômico
  const viewW = lowPower ? 2200 : 3200;
  const viewH = lowPower ? 900 : 1200;

  const gradientId = `grad-${uid}`;
  const filterId = `soft-${uid}`;
  const maskId = `fadeMask-${uid}`;

  const blendMode: React.CSSProperties["mixBlendMode"] =
    typeof CSS !== "undefined" &&
    "supports" in CSS &&
    CSS.supports("mix-blend-mode", "plus-lighter")
      ? "plus-lighter"
      : "screen";

  // Menos linhas + menos samples no modo econômico
  const ribbons: RibbonProps[] = lowPower
    ? [
        {
          lines: 22,
          spread: 5.0,
          strokeBase: 1.0,
          opacity: 0.72,
          rotate: 0,
          drift: 6,
          speed: 9.5,
          samples: 72,
        },
        {
          lines: 14,
          spread: 5.6,
          strokeBase: 0.9,
          opacity: 0.4,
          rotate: -18,
          drift: 7,
          speed: 11,
          samples: 72,
        },
        {
          lines: 12,
          spread: 5.8,
          strokeBase: 0.9,
          opacity: 0.38,
          rotate: 16,
          drift: 7,
          speed: 11.5,
          samples: 72,
        },
      ]
    : [
        {
          lines: 34,
          spread: 4.8,
          strokeBase: 1.0,
          opacity: 0.78,
          rotate: 0,
          drift: 6,
          speed: 9.5,
          samples: 96,
        },
        {
          lines: 20,
          spread: 5.6,
          strokeBase: 0.9,
          opacity: 0.44,
          rotate: -19,
          drift: 7,
          speed: 11,
          samples: 96,
        },
        {
          lines: 18,
          spread: 5.8,
          strokeBase: 0.9,
          opacity: 0.42,
          rotate: 17,
          drift: 7,
          speed: 11.5,
          samples: 96,
        },
      ];

  return (
    <BackgroundWrapper data-lowpower={lowPower ? "1" : "0"}>
      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
        shapeRendering={lowPower ? "optimizeSpeed" : "geometricPrecision"}
        textRendering="optimizeLegibility"
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1={0}
            y1={0}
            x2={viewW}
            y2={0}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0} stopColor="#ff0f57ff" />
            <stop offset={0.42} stopColor="#F04EA0" />
            <stop offset={0.52} stopColor="#30dcfaff" />
            <stop offset={0.72} stopColor="#6f4ec9ff" />
            <stop offset={1} stopColor="#e0229eff" />
          </linearGradient>

          {/* Blur bem baixo e desativado em lowPower */}
          {!lowPower && (
            <filter id={filterId} x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation="0.20" />
            </filter>
          )}

          <linearGradient id={`${maskId}-H`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="black" stopOpacity="0" />
            <stop offset="15%" stopColor="white" stopOpacity="1" />
            <stop offset="85%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="black" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`${maskId}-V`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="black" stopOpacity="0" />
            <stop offset="8%" stopColor="white" stopOpacity="1" />
            <stop offset="92%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="black" stopOpacity="0" />
          </linearGradient>
          <mask id={maskId} maskUnits="userSpaceOnUse">
            <rect
              x="0"
              y="0"
              width={viewW}
              height={viewH}
              fill={`url(#${maskId}-H)`}
            />
            <rect
              x="0"
              y="0"
              width={viewW}
              height={viewH}
              fill={`url(#${maskId}-V)`}
            />
          </mask>
        </defs>

        <g
          filter={!lowPower ? `url(#${filterId})` : undefined}
          style={{ mixBlendMode: blendMode, transform: "translateX(-10%)" }}
          mask={`url(#${maskId})`}
        >
          {ribbons.map((cfg, idx) => (
            <Ribbon
              key={idx}
              idPrefix={`${uid}-${idx}`}
              cfg={cfg}
              viewW={viewW}
              viewH={viewH}
              gradientId={gradientId}
            />
          ))}
        </g>
      </svg>
    </BackgroundWrapper>
  );
}

export default WavesBackground;
